import React from "react";
import { motion } from "framer-motion";
import { heroData } from "../data/mock";
import {
  Phone,
  MessageCircle,
  ArrowRight,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Button } from "./ui/button";

const BookingCTA = () => {
  const handleCall = () => window.open(`tel:${heroData.phone}`, "_self");

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello! I would like to book a royal cab with BAHUBALI CABS Udaipur.",
    );
    window.open(`https://wa.me/${heroData.whatsapp}?text=${message}`, "_blank");
  };

  return (
    <section className="py-24 relative overflow-hidden bg-slate-950">
      {/* 🔹 Background with Parallax Effect Look */}
      <div
        className="absolute inset-0 bg-cover bg-fixed bg-center opacity-40 scale-105"
        style={{ backgroundImage: `url(${heroData.bookingBackground})` }}
      />

      {/* 🔹 High-End Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950" />

      {/* 🔹 Luxury Glows */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-amber-600/20 rounded-full blur-[120px]" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px]" />

      <div className="max-w-5xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-[3rem] p-8 md:p-16 shadow-2xl text-center relative overflow-hidden"
        >
          {/* Animated Decorative Ring */}
          <div className="absolute -top-10 -right-10 w-40 h-40 border-4 border-amber-500/10 rounded-full" />

          {/* 🔹 24/7 Availability Badge */}
          <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-6 py-2 mb-8">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
            </span>
            <span className="text-amber-400 text-xs md:text-sm font-black uppercase tracking-[0.2em]">
              Available 24/7 • Udaipur & Outstation
            </span>
          </div>

          {/* 🔹 Premium Heading */}
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
            Ready for a <span className="italic">Royal</span> Ride with
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-200 to-amber-500">
              BAHUBALI CABS?
            </span>
          </h2>

          <p className="text-slate-300 text-lg md:text-xl mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
            Experience Udaipur's most trusted cab service. Premium cars,
            <span className="text-white font-bold"> local expertise</span>, and
            zero hidden costs.
          </p>

          {/* 🔹 High-Conversion Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button
              onClick={handleCall}
              className="w-full sm:w-auto bg-amber-500 hover:bg-white text-slate-950 text-xl font-black px-10 py-8 rounded-2xl shadow-[0_20px_50px_rgba(245,158,11,0.3)] transition-all hover:-translate-y-2 group"
            >
              <Phone className="w-6 h-6 mr-3 fill-current" />
              CALL NOW
              <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-2 transition-transform" />
            </Button>

            <Button
              onClick={handleWhatsApp}
              className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white border border-white/20 text-xl font-black px-10 py-8 rounded-2xl transition-all hover:-translate-y-2 group"
            >
              <MessageCircle className="w-6 h-6 mr-3 text-emerald-500" />
              WHATSAPP
            </Button>
          </div>

          {/* 🔹 Trust Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 pt-10 border-t border-white/5">
            <TrustItem
              icon={<Zap className="w-4 h-4 text-amber-500" />}
              text="5-Min Booking"
            />
            <TrustItem
              icon={<ShieldCheck className="w-4 h-4 text-amber-500" />}
              text="Verified Drivers"
            />
            <TrustItem
              icon={<ShieldCheck className="w-4 h-4 text-amber-500" />}
              text="Best Rates"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const TrustItem = ({ icon, text }) => (
  <div className="flex items-center justify-center gap-3">
    {icon}
    <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">
      {text}
    </span>
  </div>
);

export default BookingCTA;
